import { PDF_MIME_TYPE, splitFileName } from "@forgepoint/shared";

/** The viewer a file renders in. */
export type ViewerKind =
  | "pdf"
  | "image"
  | "model"
  | "docx"
  | "xlsx"
  | "markdown"
  | "email"
  | "unsupported";

const IMAGE_EXTENSIONS = [
  ".png",
  ".jpg",
  ".jpeg",
  ".gif",
  ".bmp",
  ".webp",
  ".svg",
];
const MODEL_MIME_TYPE = "model/gltf-binary";
// The OpenXML types only. The shared WORD_MIME_TYPES and EXCEL_MIME_TYPES also
// carry the legacy .doc and .xls types, which no viewer here renders.
const DOCX_MIME_TYPE =
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document";
const XLSX_MIME_TYPE =
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
// The .eml type only. The shared EMAIL_MIME_TYPES also carries Outlook .msg.
const EML_MIME_TYPE = "message/rfc822";

/**
 * Picks the viewer for a file from its extension first and its MIME type second.
 * A recognized extension always wins, even over a MIME type that names another
 * kind, because stored MIME types are unreliable: sandbox uploads and ZIP
 * ingestion save office files as application/octet-stream, and browsers report
 * an empty type. The URL is never read, so a key that contains "pdf" cannot
 * force PDF rendering.
 * @param fileType - The stored MIME type of the file
 * @param fileName - The file name, with or without a folder path
 * @returns The viewer kind that can render the file
 */
export function resolveViewerKind({
  fileType,
  fileName,
}: {
  fileType: string;
  fileName: string;
}): ViewerKind {
  const extension = splitFileName(fileName).extension.toLowerCase();
  return (
    kindFromExtension(extension) ?? kindFromMimeType(fileType) ?? "unsupported"
  );
}

/** Maps a lower-cased extension, dot included, to a viewer kind. */
function kindFromExtension(extension: string): ViewerKind | undefined {
  if ([".md", ".markdown"].includes(extension)) {
    return "markdown";
  }
  if (extension === ".pdf") {
    return "pdf";
  }
  if (extension === ".glb") {
    return "model";
  }
  if (extension === ".docx") {
    return "docx";
  }
  if (extension === ".xlsx") {
    return "xlsx";
  }
  if (extension === ".eml") {
    return "email";
  }
  if (IMAGE_EXTENSIONS.includes(extension)) {
    return "image";
  }
  return undefined;
}

/** Maps a stored MIME type to a viewer kind. */
function kindFromMimeType(fileType: string): ViewerKind | undefined {
  if (
    ["text/markdown", "text/x-markdown"].includes(
      fileType.split(";")[0].trim().toLowerCase(),
    )
  ) {
    return "markdown";
  }
  if (fileType === PDF_MIME_TYPE) {
    return "pdf";
  }
  if (fileType === MODEL_MIME_TYPE) {
    return "model";
  }
  if (fileType === DOCX_MIME_TYPE) {
    return "docx";
  }
  if (fileType === XLSX_MIME_TYPE) {
    return "xlsx";
  }
  if (fileType === EML_MIME_TYPE) {
    return "email";
  }
  if (fileType.startsWith("image/")) {
    return "image";
  }
  return undefined;
}
